import { getWindow } from "@faire/web--source/common/globals/getWindow";
import * as React from "react";

import { PerformanceMarkStartTime } from "../TrackPerformanceBreakdown/markPageJsExecuted";

import { LoadTimingOptions } from "./types";
import { UserPerceivedLoadTiming } from "./UserPerceivedLoadTiming";
import {
  useTrackCumulativeLayoutShift,
  useTrackFirstContentfulPaint,
  useTrackInteractionToNextPaint,
  useTrackLargestContentfulPaint,
} from "./useTrackWebVitals";

export const DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE = {
  __unique_obj: "DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE",
};

export interface UPLTHookOptions extends LoadTimingOptions {
  /* When `true` it indicates that the page is in a loading state,
   * The UPLT measurement will wait for a value of `false` before starting.
   * Additionally when opting-out of `pageLoadKey` restart behaviour, a change
   * of `isPageLoading` from `false` to `true` will cancel the existing
   * measurement and start a new one.
   */
  isPageLoading: boolean;

  /* If `false`, the UPLT measurement is disabled. */
  enabled?: boolean;

  /* The value of `dataUpdatedAt` returned by useQuery(). Used to track the duration
   * of the data fetch. */
  dataUpdatedAt?: number;

  /* The PerformanceMarkStartTime returned by `markPageJsExecuted()`. Used to track the
   * duration of page JS download & execution. */
  pageJsExecutedMark: PerformanceMarkStartTime | undefined;
}

export const useUserPerceivedLoadTimingWithKey = (
  options: UPLTHookOptions
): React.RefCallback<HTMLElement> => {
  const pageMountTimeRef = React.useRef<number | undefined>(undefined);
  const recorderRef = React.useRef<UserPerceivedLoadTiming | undefined>(
    undefined
  );

  const { enabled, pageLoadKey, isPageLoading } = options;

  const didKeyChange = useDidValueChange(pageLoadKey);
  const didIsPageLoadingChange = useDidValueChange(isPageLoading);
  const loadType =
    !isPageLoading && recorderRef.current
      ? recorderRef?.current?.loadType
      : undefined;
  useTrackCumulativeLayoutShift(options.name, loadType);
  useTrackInteractionToNextPaint(options.name, loadType);

  useTrackFirstContentfulPaint(options.name, !!options.actionStartTime);
  useTrackLargestContentfulPaint(options.name, !!options.actionStartTime);

  if (enabled !== false) {
    const shouldRestart =
      pageLoadKey === DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE
        ? didIsPageLoadingChange && isPageLoading
        : didKeyChange;

    if (shouldRestart) {
      recorderRef.current?.cancel();
      recorderRef.current = new UserPerceivedLoadTiming(options, {
        pageMountTime: pageMountTimeRef.current,
        dataUpdatedAt: options.dataUpdatedAt,
        pageJsExecutedMark: options.pageJsExecutedMark,
      });
    }
  }

  React.useLayoutEffect(() => {
    pageMountTimeRef.current = getWindow()?.performance.now();
    recorderRef.current?.notifyPageMount();
    return () => {
      recorderRef.current?.notifyPageUnmount();
    };
  }, []);

  React.useLayoutEffect(() => {
    if (pageLoadKey !== DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE) {
      recorderRef.current?.notifyReadyToFetchData();
    }
  }, [pageLoadKey]);

  React.useLayoutEffect(() => {
    if (
      pageLoadKey === DEPRECATED_RESTART_WHEN_ISPAGELOADING_BECOMES_TRUE &&
      isPageLoading
    ) {
      recorderRef.current?.notifyReadyToFetchData();
    }
  }, [pageLoadKey, isPageLoading]);

  React.useLayoutEffect(() => {
    if (!isPageLoading) {
      recorderRef.current?.notifyRenderedWithData();
    }
  }, [pageLoadKey, isPageLoading]);

  React.useEffect(() => {
    recorderRef.current?.setDataUpdatedAt(options.dataUpdatedAt);
  }, [options.dataUpdatedAt]);

  React.useEffect(() => {
    recorderRef.current?.setPageJsExecutedMark(options.pageJsExecutedMark);
  }, [options.pageJsExecutedMark]);

  React.useEffect(() => {
    recorderRef.current?.setIgnoreImages(options.ignoreImages ?? false);
  }, [options.ignoreImages]);

  React.useEffect(() => {
    recorderRef.current?.setNumComponents(options.numComponents);
  }, [options.numComponents]);

  React.useEffect(() => {
    /* This useEffect handles two scenarios when a UPLT measurement should be started:
     * - `isPageLoading` changes from `true` to `false` (ie. fetch with cache miss).
     * - `pageLoadKey` changes and `isPageLoading` is `false` (ie. useQuery cache hit).
     */
    if (!isPageLoading && recorderRef.current) {
      recorderRef.current.start();
    }
  }, [pageLoadKey, isPageLoading]);

  if (isPageLoading) {
    /* In the scenario where `isPageLoading === true` we use a no-op ref callback.
     * This prevents `refCallback` from being passed `null` if the target
     * component unmounts for the loading state, which would cancel the
     * measurement with status TARGET_COMPONENT_UNMOUNTED.
     */
    return emptyRefCallback;
  } else if (recorderRef.current) {
    /* Wait until `isPageLoading === false` to return the real `refCallback`.
     * When the function identity changes, React will call the new function with each of
     * the already-mounted elements.
     */
    return recorderRef.current.refCallbackWithOptions;
  } else {
    /* In the case that `enabled === false` and there is no UPLT instance,
     * return the no-op ref callback.
     */
    return emptyRefCallback;
  }
};

function useDidValueChange<T>(value: T): boolean {
  const previousValueRef = React.useRef<T | undefined>(undefined);
  const didChange = previousValueRef.current !== value;
  previousValueRef.current = value;

  return didChange;
}

function emptyRefCallback() {}
